import { GetStaticProps } from 'next';
import React from 'react';
import { ButtonVariants } from '../../../components/Atoms/Button';
import { StickyCTA } from '../../../components/CTA/StickyCTA';
import { CTA_KEY, FILE_NOW_TEXT } from '../../../components/CTA/constants';
import { FaqComponent } from '../../../components/Common/FAQS';
import GridCard from '../../../components/Common/GridCards';
import TableOfContents from '../../../components/Common/TableOfContents';
import { ContentComponent } from '../../../components/IfscCode/ContentComponent';
import SearchIFSC from '../../../components/IfscCode/SearchIFSC';
import { ClearInNavbar } from '../../../components/Navbar/ClearNavbar';
import { TopCTA } from '../../../components/cms/layout/components';
import {
  b2cData,
  NAV_TYPES,
} from '../../../components/cms/layout/components/header';
import {
  IfscContents,
  IFSCLandingFAQS,
  popularBanks,
} from '../../../constants/IfscConstants';
import { faqSchema, ORG_SCHEMA } from '../../../constants/componentStaticData';
import { Meta } from '../../../layout/Meta';
import { getCachedCTAData } from '../../../utils/cta-cache';
import { staticPropsWrapper } from '../../../utils/static-pages/utils';
import { handleTrack } from '../../../utils/tracker';

interface ifscPropsInterface {
  top_cta: string;
}

const IfscPage = ({ top_cta }: ifscPropsInterface): JSX.Element => {
  const captureEvent = (e: React.MouseEvent<Element>) => {
    handleTrack({
      label: 'top_cta',
      obj: e.target,
    });
  };

  return (
    <div>
      <ClearInNavbar data={b2cData} />
      <Meta
        title="IFSC Code Search India - Search &amp; Find IFSC &amp; MICR for all Bank Branches"
        description="Find the List of All Bank IFSC &amp; MICR Codes with Branch Details. Indian Financial System Code (IFSC) is a 11-Digit Alphanumeric Code Used to Identify Bank Branches. IFSC Code is Used to Transfer Money via NEFT, RTGS, &amp; IMPS"
        canonical="https://cleartax.in/s/ifsc-code/"
        ampURL="https://cleartax.in/ifsc-code/amp"
      />
      <TopCTA CTA={top_cta} onClickCB={captureEvent} />
      <div className="wg-container md:hidden lg:hidden xl:hidden mt-12">
        <h1 className="text-s-48 sm:text-s-24 sm:mt-6">
          IFSC Codes in India: List of IFSC code, MICR code and addresses of all
          banks
        </h1>
        <div className="py-12">
          <SearchIFSC />
        </div>
      </div>
      <div className="wg-container py-12 flex sm:flex-col-reverse mx-auto sm:py-2">
        <div className="w-3/4 pr-12 sm:pr-0 sm:w-full sm:mt-6">
          <div className="sm:hidden">
            <h1 className="text-s-48 sm:text-s-24">
              IFSC Codes in India: List of IFSC code, MICR code and addresses of
              all banks
            </h1>
            <div className="py-12">
              <SearchIFSC />
            </div>
          </div>
          {IfscContents.map((content, index: number) => (
            <ContentComponent
              key={'contents' + index}
              id={content?.id}
              header={content?.header}
              para1={content?.para1}
              list={content?.list}
              table={content?.table}
              para2={content?.para2}
            />
          ))}
          <FaqComponent FAQS={IFSCLandingFAQS} />

          <div>
            <h2 className="text-s-40 sm:text-s-20">Popular Banks</h2>
            <GridCard lists={popularBanks} />
          </div>
        </div>

        <div className="w-1/4 pl-12 text-font-500 sm:pl-0 sm:w-full right-articles-section">
          <TableOfContents data={IfscContents} />
        </div>
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema(IFSCLandingFAQS)),
        }}
      ></script>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ORG_SCHEMA) }}
      ></script>
      <StickyCTA
        buttonText={FILE_NOW_TEXT}
        buttonStyle={ButtonVariants.bluefilled}
        type={NAV_TYPES.B2C}
      />
    </div>
  );
};

export const getStaticProps: GetStaticProps = staticPropsWrapper(
  __filename,
  async () => {
    // Use cached CTA data to avoid repeated API calls during build
    try {
      const cta = await getCachedCTAData(CTA_KEY.IFSC_CTA);
      return {
        props: {
          top_cta: (cta as { cta_html?: string } | undefined)?.cta_html ?? '',
        },
      };
    } catch (error: unknown) {
      console.error(
        'Error fetching cached CTA data:',
        error instanceof Error ? error.message : 'Unknown error'
      );
      return { props: { top_cta: '' } };
    }
  }
);

export default IfscPage;
